import React, { useEffect, useState } from 'react';
import { Loader2, Send, ShoppingCart, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const AbandonedCartsAdmin = () => {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/abandoned-carts', { params: { hours } })
      .then(r => { setCarts(r.data); setSelected({}); })
      .catch(() => toast.error('Failed to load carts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);

  const anySelected = Object.values(selected).some(Boolean);
  const toggleAll = () => {
    const allOn = carts.every(c => selected[c.user_id]);
    const next = {};
    if (!allOn) carts.forEach(c => { next[c.user_id] = true; });
    setSelected(next);
  };

  const sendSelected = async () => {
    const user_ids = anySelected ? Object.keys(selected).filter(k => selected[k]) : null;
    setSending(true);
    try {
      const r = await api.post('/admin/abandoned-carts/send', { user_ids, hours });
      toast.success(`Sent ${r.data.sent}/${r.data.total_candidates} · failed ${r.data.failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-abandoned-carts">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-[#12263A]">Abandoned Carts</h2>
          <p className="text-xs text-neutral-600 mt-1">
            Customers with items sitting in the basket for more than the selected window. Send them the <b>Abandoned Cart</b> template to bring them back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-[#12263A] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Idle for</label>
          <select
            value={hours}
            onChange={e => setHours(Number(e.target.value))}
            data-testid="abandoned-hours-select"
            className="text-xs border border-neutral-300 rounded-sm px-2 py-1"
          >
            <option value={1}>1 hour</option>
            <option value={6}>6 hours</option>
            <option value={12}>12 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
          <button
            onClick={load}
            data-testid="abandoned-refresh-btn"
            className="text-xs border border-neutral-300 hover:border-[#F46A25] font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1"
          ><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25]" /></div>
      ) : carts.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-8 text-center text-sm text-neutral-500" data-testid="abandoned-empty">
          <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
          No abandoned carts in this window.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="p-2 w-8"><input type="checkbox" checked={carts.length > 0 && carts.every(c => selected[c.user_id])} onChange={toggleAll} data-testid="abandoned-toggle-all" /></th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Email</th>
                <th className="text-center p-2">Items</th>
                <th className="text-right p-2">Cart total</th>
                <th className="text-right p-2">Last updated</th>
              </tr>
            </thead>
            <tbody>
              {carts.map(c => (
                <tr key={c.user_id} className="border-t border-neutral-100 hover:bg-orange-50/40" data-testid={`abandoned-row-${c.user_id}`}>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!selected[c.user_id]}
                      onChange={e => setSelected(s => ({ ...s, [c.user_id]: e.target.checked }))}
                      data-testid={`abandoned-check-${c.user_id}`}
                    />
                  </td>
                  <td className="p-2 font-semibold text-[#12263A]">{c.user_name || '—'}</td>
                  <td className="p-2 text-neutral-700 font-mono text-xs">{c.user_email}</td>
                  <td className="p-2 text-center">{c.item_count}</td>
                  <td className="p-2 text-right font-bold">£{c.total.toFixed(2)}</td>
                  <td className="p-2 text-right text-neutral-500 text-xs">{c.updated_at ? new Date(c.updated_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={sendSelected}
          disabled={sending || carts.length === 0}
          data-testid="abandoned-send-btn"
          className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs px-4 py-2 rounded-sm disabled:opacity-60 flex items-center gap-1"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {anySelected ? 'Send to selected' : `Send to all (${carts.length})`}
        </button>
        <span className="text-[11px] text-neutral-500">Uses the <b>Abandoned Cart</b> template from Email Templates.</span>
      </div>
    </div>
  );
};

export default AbandonedCartsAdmin;
